import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { btnGhost, btnPrimary, inputCls } from "@/components/TopBar";

export const SpeedRangeDialog = ({ vehicles, onClose, onSaved }) => {
  const [selected, setSelected] = useState(vehicles.map((v) => v.id));
  const [from, setFrom] = useState(2);
  const [to, setTo] = useState(3);
  const [busy, setBusy] = useState(false);

  const allSelected = selected.length === vehicles.length && vehicles.length > 0;
  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return toast.error("Select at least one truck");
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles/speed-range", {
        vehicle_ids: selected,
        min_kmh: Number(from),
        max_kmh: Number(to),
      });
      toast.success(`${data.updated} truck(s) now cruise at ${data.min_kmh}-${data.max_kmh} km/h`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1550] flex items-start justify-center overflow-y-auto p-4 py-10"
      data-testid="speed-range-dialog"
    >
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          type="button"
          data-testid="close-speed-dialog-btn"
          onClick={onClose}
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">fleet control</p>
        <h2 className="font-head text-2xl font-black tracking-tight">SPEED RANGE</h2>
        <p className="mt-1 text-[11px] text-[#A1A1AA]">
          Average cruising speed band for the selected trucks. Each truck picks a speed inside this range
          while it is running.
        </p>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label className="overline">trucks · {selected.length} selected</label>
            <button
              type="button"
              data-testid="speed-select-all-btn"
              onClick={() => setSelected(allSelected ? [] : vehicles.map((v) => v.id))}
              className={btnGhost}
            >
              {allSelected ? "CLEAR ALL" : "SELECT ALL"}
            </button>
          </div>
          <div
            className="doot-scroll mt-2 max-h-48 overflow-y-auto border border-white/15 bg-[#121212]"
            data-testid="speed-vehicle-list"
          >
            {vehicles.map((v) => (
              <label
                key={v.id}
                data-testid={`speed-vehicle-option-${v.plate_number}`}
                className="flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-2 text-[11px] hover:bg-[#1A1A1A]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(v.id)}
                  onChange={() => toggle(v.id)}
                  className="h-3 w-3 accent-[#00F0FF]"
                />
                <span className="font-head font-bold">{v.plate_number}</span>
                <span className="ml-auto text-[9px] tracking-[0.12em] text-[#52525B]">
                  {v.cruise_min_kmh ? `${v.cruise_min_kmh}-${v.cruise_max_kmh} KM/H` : "2-3 KM/H"}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <label className="overline mb-2 block">from km/h</label>
            <input
              data-testid="speed-from-input"
              type="number"
              min="1"
              max="120"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">to km/h</label>
            <input
              data-testid="speed-to-input"
              type="number"
              min="1"
              max="120"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="submit-speed-range-btn"
          className={`${btnPrimary} mt-6 w-full py-3`}
        >
          {busy ? "applying" : `apply to ${selected.length || 0} truck(s)`}
        </button>
      </form>
    </div>
  );
};
